/**
 * Keeps the home page's unread counts current while the page is shown: read on
 * mount, when the settings change, when master signals a notification on the
 * shared socket, when the window comes back into view and every
 * `refresh_seconds`. The timer covers what no notification announces: a muted
 * channel, a message read on another device, a mail while its notifications
 * are off.
 *
 * A failed read keeps the count last known; nothing is shown before the first
 * answer. An app that answers 401 even after the token refresh (mail for an
 * account without a mailbox) is not asked again while the page stays: every
 * ask would refresh the session's token for nothing.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch, type ComputedRef, type Ref } from 'vue';
import { useAccountConfig, useNxSocket } from '@blpde/connect';
import { fetchWithAuth, getApiOrigin } from '@blpde/connect/auth';
import {
  chatUnreadQuery,
  homeUnreadSettings,
  mailUnreadQuery,
  mutedChatChannels,
  type HomeUnreadSettings,
  type HomeUnreadShellDocument,
} from './unreadCounts';

const SOCKET_SETTLE_MS = 500;

class CountRefused extends Error {}

async function readCount(module: string, path: string, field: string): Promise<number> {
  const response = await fetchWithAuth(
    `${getApiOrigin()}/v1${path}`,
    { headers: { Accept: 'application/json', 'BLP-Module': module } },
    { maxRetries: 0 }
  );
  if (response.status === 401) throw new CountRefused(module);
  if (!response.ok) throw new Error(`${module} count: ${response.status}`);
  const count = Number(((await response.json()) as Record<string, unknown>)[field]);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

export interface HomeUnreadCounts {
  settings: ComputedRef<HomeUnreadSettings>;
  /** The count per app id, for the apps that have one. */
  counts: Ref<Record<string, number>>;
}

/** `countable(appId)`: the account may use the app and its module answers. */
export function useHomeUnreadCounts(countable: (appId: string) => boolean): HomeUnreadCounts {
  const config = useAccountConfig<HomeUnreadShellDocument>('shell');
  const socket = useNxSocket();
  const settings = computed(() => homeUnreadSettings(config.effective.value));
  const counts = ref<Record<string, number>>({});

  // What each app's count asks; null asks nothing and shows no count.
  const queries = computed<Record<string, string | null>>(() => {
    const current = settings.value;
    const on = current.enabled;
    return {
      chat:
        on && current.chat && countable('chat')
          ? chatUnreadQuery(current, mutedChatChannels(config.effective.value))
          : null,
      mail: on && current.mail && countable('mail') ? mailUnreadQuery(current) : null,
    };
  });

  const refused = new Set<string>();
  let serial = 0;
  async function refresh(): Promise<void> {
    const mine = ++serial;
    const { chat, mail } = queries.value;
    const read = (app: string, query: string | null, path: string, field: string) =>
      query === null || refused.has(app)
        ? Promise.resolve({ app, count: null })
        : readCount(app, `${path}${query ? `?${query}` : ''}`, field).then(
            (count) => ({ app, count }),
            (err: unknown) => {
              if (err instanceof CountRefused) refused.add(app);
              return { app, count: counts.value[app] ?? null };
            }
          );
    const answers = await Promise.all([
      read('chat', chat, '/unread', 'total'),
      read('mail', mail, '/mail/unread', 'count'),
    ]);
    // An older answer never overwrites a newer one (a setting just changed).
    if (mine !== serial) return;
    const next: Record<string, number> = {};
    for (const { app, count } of answers) if (count !== null) next[app] = count;
    counts.value = next;
  }

  let timer: ReturnType<typeof setInterval> | undefined;
  function startTimer(): void {
    clearInterval(timer);
    timer = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, settings.value.refreshSeconds * 1000);
  }

  let settle: ReturnType<typeof setTimeout> | undefined;
  function refreshSoon(): void {
    clearTimeout(settle);
    settle = setTimeout(() => void refresh(), SOCKET_SETTLE_MS);
  }

  function onVisibility(): void {
    if (document.visibilityState === 'visible') void refresh();
  }

  let unsubscribe: (() => void) | undefined;
  onMounted(() => {
    void refresh();
    startTimer();
    document.addEventListener('visibilitychange', onVisibility);
    unsubscribe = socket?.channel('master').on('event', (env) => {
      if (typeof env?.evt === 'string' && env.evt.startsWith('notification.')) refreshSoon();
    });
  });

  watch(queries, () => void refresh(), { deep: true });
  watch(() => settings.value.refreshSeconds, startTimer);

  onBeforeUnmount(() => {
    serial++;
    clearInterval(timer);
    clearTimeout(settle);
    document.removeEventListener('visibilitychange', onVisibility);
    unsubscribe?.();
  });

  return { settings, counts };
}
